import React from 'react';
// Context
import { SocialAccount } from 'context/OnboardingContext';
import { SocialMediaType } from 'api/graphql-global-types';
// Constants
import { SOCIALS_OPTIONS } from 'constants/socials';
// Helpers
import { getSocialProfileLinkError } from 'helpers/socialMediaLink';
// Ui
import Modal from 'ui3/Modal/Modal';
import Button from 'ui3/Button/Button';
import SocialsInput from 'ui3/SocialsInput/SocialsInput';
// Styles
import styles from './AddSocialAccountModal.module.scss';

type SocialAccountErrors = {
  platform?: string;
  link?: string;
};

type AddSocialAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
  platformOptions?: { value: SocialMediaType; label: string }[];
  usedPlatforms?: SocialMediaType[];
  errors?: SocialAccountErrors;
  value?: Partial<SocialAccount>;
  onChange: (value: Partial<SocialAccount>) => void;
  onConfirm: () => void;
  onCancel?: () => void;
};

const AddSocialAccountModal = ({
  isOpen,
  onClose,
  platformOptions = SOCIALS_OPTIONS,
  usedPlatforms = [],
  errors,
  value,
  onChange,
  onConfirm,
  onCancel,
}: AddSocialAccountModalProps) => {
  const hasPlatform = Boolean(value?.platform);
  const hasLink = Boolean(value?.link?.trim());
  // Validated here rather than by each caller so onboarding and account settings cannot diverge
  const linkFormatError = getSocialProfileLinkError(
    value?.platform,
    value?.link
  );
  const linkError = linkFormatError || errors?.link;
  const hasErrors = Boolean(errors?.platform || linkError);
  const isFormValid = hasPlatform && hasLink && !hasErrors;

  const handleAddAccount = () => {
    if (!isFormValid) {
      return;
    }
    onConfirm();
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        onClose();
        onCancel?.();
      }}
      title="Add Social Account"
      size="small"
    >
      <div className={styles.form}>
        <SocialsInput
          platformOptions={platformOptions.map((option) => ({
            ...option,
            disabled: usedPlatforms.includes(option.value),
          }))}
          value={value}
          onChange={onChange}
          platformError={errors?.platform}
          linkError={linkError}
        />
        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={handleAddAccount}
            block
            disabled={!isFormValid}
          >
            Add Social Account
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddSocialAccountModal;
